import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isEmpty, isNil, orderBy, upperFirst } from 'lodash';
import moment from 'moment-timezone';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AllDataUser,
  computeAllEntityStats,
  EntityStats,
  EntityStatsDisplayWindow,
  EntityStatsSchema,
  EntityStatsType,
  EventType,
  HoursOverTime,
  OrgData,
} from '@pwp-common';

import { KVPair } from '../../../../common/objects/kvpair';
import { NgChanges } from '../../../../common/objects/ng-changes';
import { makePTableCols } from '../../../../common/p-table/make-p-table-cols';
import { EventsService } from '../../../../services/event/events/events.service';
import { EntityStatsDisplayWindowChange } from '../../generic/entity-stats-display-window-change';
import { createAggregateRows } from '../helpers/create-aggregate-rows/create-aggregate-rows';
import { createEventHoursRow } from '../helpers/create-event-hours-row';
import { EventHoursRow, EventHoursRowWithAllDataUser } from '../helpers/interfaces';

@UntilDestroy()
@Component({
  selector: 'app-event-hours-table',
  templateUrl: './event-hours-table.component.html',
  styleUrls: ['./event-hours-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventHoursTableComponent implements OnChanges {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Input / Output
  /////////////////////////////////////////////////////////////////////////////////////////////

  @Input() public orgData: OrgData;

  @Input() public entityStatsMap: Map<string, EntityStats>;

  @Input() public allDataUserMap: Map<string, AllDataUser>;

  @Input() public windowObj: EntityStatsDisplayWindowChange;

  @Input() public eventTypes: EventType[] = [];

  ///////////////////////////////////////////////////////////////////////
  // Variables
  ///////////////////////////////////////////////////////////////////////

  public customEntityStatsMap: Map<string, EntityStats>;

  public selectedEventTypes: KVPair<EventType>[] = [];

  public loading = false;

  public cols = [];

  public tableRows$ = new BehaviorSubject<EventHoursRow[]>([]);

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    private translocoService: TranslocoService,
    private eventsService: EventsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public async ngOnChanges(changes: NgChanges<EventHoursTableComponent>): Promise<void> {
    if (!isNil(changes.eventTypes)) {
      this.selectedEventTypes = this.eventTypes?.map((value, ind) => new KVPair({ id: ind, value }));
    }
    await this.getData();
  }

  ///////////////////////////////////////////////////////////////////////
  // Get Data
  ///////////////////////////////////////////////////////////////////////

  public async getData() {
    console.log('EventHoursTableComponent.getData: Starting', this.windowObj);

    // Loading will be set to false by updateUIWithEntityStats
    this.loading = true;

    /**
     * Case 1: Non-Custom Window Type
     *
     * - Data already obtained. Just refresh UI.
     */
    if (this.windowObj?.type !== EntityStatsDisplayWindow.custom) {
      this.updateUIWithEntityStats();
      console.log('EventHoursTableComponent.getData: Completed', { windowObj: this.windowObj });
      return;
    }

    /**
     * Case 2: Custom Window Type
     *
     * - Get data, and refresh UI.
     */
    const selectedEventTypes = this.selectedEventTypes.map((z) => z.value.getInternalName());

    await firstValueFrom(
      this.eventsService
        .getEventsWithStartOrEndInRange(
          this.windowObj.windowStart,
          this.windowObj.windowEnd,
          true,
          ...selectedEventTypes,
        )
        .pipe(
          map((events) => {
            this.customEntityStatsMap = computeAllEntityStats(
              [],
              events,
              this.allDataUserMap,
              this.orgData.getTimezone(),
            );
          }),
          map(() => {
            this.updateUIWithEntityStats();
            this.changeDetectorRef.detectChanges();
            console.log('EventHoursTableComponent.getData: Completed', { windowObj: this.windowObj });
          }),
        ),
    );
  }

  ///////////////////////////////////////////////////////////////////////
  // Update UI
  ///////////////////////////////////////////////////////////////////////
  private getActiveEntityStatsMap(): Map<string, EntityStats> {
    if (this.windowObj?.type === EntityStatsDisplayWindow.custom) {
      return this.customEntityStatsMap;
    }
    return this.entityStatsMap;
  }

  public updateUIWithEntityStats() {
    console.log('EventHoursTableComponent.updateUIWithEntityStats: Starting');
    if (isNil(this.windowObj)) {
      console.log('EventHoursTableComponent.updateUIWithEntityStats: Window obj is nil. Returning');
      this.loading = false;
      return;
    }
    this.loading = true;
    const selectedEventTypesArray = this.selectedEventTypes.map((z) => z.value);
    const months: Set<string> = new Set();

    const oneUserEntityStats = Array.from(this.getActiveEntityStatsMap()?.values() ?? [])?.filter(
      (entityStats) => entityStats.getType() === EntityStatsType.oneUser,
    );

    if (isEmpty(oneUserEntityStats)) {
      this.loading = false;
      this.updateCols(new Set());
      console.log('EventHoursTableComponent.updateUIWithEntityStats: Entity stats not found. Returning');
      return;
    }

    const rowsWithAllDataUser: EventHoursRowWithAllDataUser[] = [];

    for (const entityStats of oneUserEntityStats) {
      const hoursForOneEntity = new HoursOverTime(
        entityStats as EntityStats,
        this.windowObj.type,
        this.allDataUserMap,
        selectedEventTypesArray,
      );

      const allDataUser = this.allDataUserMap.get(entityStats.getId());

      const row = createEventHoursRow({
        entityId: entityStats.getId(),
        hoursOverTime: hoursForOneEntity,
        allDataUser,
      });

      rowsWithAllDataUser.push({ row, allDataUser });
      hoursForOneEntity.monthStrs.forEach((z) => months.add(z));
    }
    // const aggregateRows = createAggregateRows({ rowsWithMetadata, months });

    this.updateCols(months);

    const aggregateRows = createAggregateRows({ rowsWithAllDataUser, months });

    const userRows = rowsWithAllDataUser.map((item) => item.row);
    const sortedUserRows = orderBy(userRows, (z) => z.displayName.sortValue, ['asc']);

    const allRows = [...sortedUserRows, ...aggregateRows];

    this.tableRows$.next(allRows);
    this.loading = false;
    console.log('EventHoursTableComponent.updateUIWithEntityStats: Completed');
  }

  /**
   * Update the list of columns with the given set of months.
   *
   * @param months
   */
  private updateCols(months: Set<string>) {
    const sortedMonths = orderBy(
      Array.from(months),
      (month) => moment(month, EntityStatsSchema.Constants.monthFormat).valueOf(),
      ['desc'],
    );

    const cols = ['displayName', 'totalHours', 'isAdmin', 'orgNote'];

    for (const monthStr of sortedMonths) {
      // Hours
      const numHoursKey = HoursOverTime.getKeyName(HoursOverTime.NUM_HOURS_POSTFIX, monthStr);
      cols.push(numHoursKey);
      const headerKeyHours = `col${upperFirst(numHoursKey)}`;
      this.translocoService.setTranslationKey(`event-hours-table.${headerKeyHours}`, `Hours: ${monthStr}`, 'en');
      this.translocoService.setTranslationKey(`event-hours-table.${headerKeyHours}`, `Horas: ${monthStr}`, 'es');

      // Events
      const numEventsKey = HoursOverTime.getKeyName(HoursOverTime.NUM_EVENTS_POSTFIX, monthStr);
      cols.push(numEventsKey);
      const headerKeyEvents = `col${upperFirst(numEventsKey)}`;
      this.translocoService.setTranslationKey(`event-hours-table.${headerKeyEvents}`, `Shifts: ${monthStr}`, 'en');
      this.translocoService.setTranslationKey(`event-hours-table.${headerKeyEvents}`, `Turnos: ${monthStr}`, 'es');
    }

    this.cols = makePTableCols({ columns: cols, translationScope: 'event-hours-table' });
  }
}
